import {
    generateClient,
    GraphQLQuery,
} from 'aws-amplify/api';

import {
    getDnsZone,
    getProviderConnectionCapabilities,
    listDnsZones,
    listProviderConnectionsForZoneCreate,
} from './queries';

import {
    createDnsZone,
    deleteDnsZone,
} from './mutations';

import {
    CreateDnsZone,
    CreateDnsZoneInput,
    DeleteDnsZone,
    DeleteDnsZoneInput,
    DeleteDnsZoneResult,
    DnsZone,
    DnsZonesConnectionResult,
    GetDnsZone,
    ListDnsZones,
    ListProviderConnections,
    ZoneCreateConnection,
    ZoneWriteCapabilities,
} from '../types';

const client = generateClient();

// FNP-1191 — READ HALF. No add, no update, no delete: the FNP-1184 schema has
// no zone mutation to call, and the port methods behind one do not exist yet.
//
// ⚠️ Errors are RE-THROWN, not swallowed into a notification here — the same
// call the hosted-zones module makes and for the same reason. Catching and
// returning undefined makes a failed fetch indistinguishable from an empty
// result to React Query: `isError` stays false and the table renders its empty
// state, so "the API is down" and "you have no zones" look identical.
//
// ⚠️ A TRANSPORT ERROR AND A `failures[]` ENTRY ARE DIFFERENT THINGS and this
// module must not merge them. A rejection means the query itself did not
// answer — nothing is known. A `failures` entry means the query DID answer:
// these zones are real and complete, AND these connections could not be read.
// The first is an error state; the second is success-with-a-warning.

export const fetchRecords = async (
    connectionId: string | undefined,
    limit: number,
    nextToken: string | null,
): Promise<DnsZonesConnectionResult> => {

    const response = await client.graphql<GraphQLQuery<ListDnsZones>>({
        query: listDnsZones,
        variables: {
            connectionId,
            limit,
            nextToken,
        },
    });

    return {
        items: response.data?.listDnsZones?.items ?? [],
        // ⚠️ `?? null` and NOT `|| null`: an empty-string token is not a thing
        // this API returns, but coercing one to null would end pagination early.
        nextToken: response.data?.listDnsZones?.nextToken ?? null,
        // The schema says `[DnsConnectionFailure!]!` — empty, never null. The
        // default is here anyway so a partial/degraded response cannot make
        // `failures.length` throw in the banner.
        failures: response.data?.listDnsZones?.failures ?? [],
    };
};

// ⚠️ BOTH arguments, always. `getDnsZone(connectionId: ID!, id: ID!)` — passing
// the id alone is a schema validation error, not a lookup that returns null.
export const fetch = async (
    connectionId: string,
    id: string,
): Promise<DnsZone | undefined> => {

    const response = await client.graphql<GraphQLQuery<GetDnsZone>>({
        query: getDnsZone,
        variables: {
            connectionId,
            id,
        },
    });

    return response.data?.getDnsZone;
};

type GetProviderConnectionCapabilities = {
    getProviderConnection?: {
        id: string,
        status: string,
        capabilities?: ZoneWriteCapabilities | null,
    } | null
}

// The capability snapshot for ONE connection (FNP-1181).
//
// This is the read half of the capability-driven hiding rule: the CRUD ticket
// drops controls in behind `useZoneWriteCapabilities`, and this is what tells
// it whether to. Undefined means "could not be determined" and the hook treats
// that as NOT permitted — see useZoneWriteCapabilities for why that direction.
export const fetchConnectionCapabilities = async (
    connectionId: string,
): Promise<ZoneWriteCapabilities | undefined> => {

    const response = await client.graphql<GraphQLQuery<GetProviderConnectionCapabilities>>({
        query: getProviderConnectionCapabilities,
        variables: {id: connectionId},
    });

    const connection = response.data?.getProviderConnection;

    if (!connection) {
        return undefined;
    }

    // ⚠️ A connection that is not ACTIVE reports NO write capability, whatever
    // its stored snapshot says. The snapshot records what the credential proved
    // AT CONNECT TIME; an INVALID or UNREACHABLE status is the provider saying
    // that credential does not work NOW. Offering a write control against it
    // would produce a failure at submit rather than an honest absence.
    if (connection.status !== 'ACTIVE') {
        return undefined;
    }

    return connection.capabilities ?? undefined;
};

// ─── FNP-1191 CRUD half: the write path (FNP-1219 mutations) ───
//
// ⚠️ ERRORS ARE RE-THROWN HERE TOO, and for a sharper reason than on the reads.
//
// A rejected write carries the provider's REFUSAL — ZONE_NOT_EMPTY above all —
// and that refusal is the most valuable thing the call produced. Catching it
// into a notification here would strip the class before any caller could branch
// on it, and the delete guard this ticket exists to build would have nothing to
// read. The mutation hooks classify it (services/writeErrors) and the UI
// renders the class plus the provider's verbatim text.
//
// ⚠️ NO `add`/`update`/`deleteRecord` NAMES. The pattern doc's generic trio
// assumes an entity with all three; this one has no update at all, and naming a
// function `update` that does not exist would invite a caller to look for it.

/**
 * Create a zone through a connection.
 *
 * Returns the whole `DnsZone` — the schema chose that so a caller never has to
 * construct one. ⚠️ `nameservers` MAY BE EMPTY, and empty means "not yet
 * assigned" rather than "publishes none": Route 53 assigns at creation, a
 * provider that activates a zone asynchronously may not.
 */
export const createZone = async (
    input: CreateDnsZoneInput,
): Promise<DnsZone | undefined> => {

    const response = await client.graphql<GraphQLQuery<CreateDnsZone>>({
        query: createDnsZone,
        variables: {input},
    });

    return response.data?.createDnsZone;
};

/**
 * Delete a zone through the connection that owns it.
 *
 * ⚠️ THE PROVIDER IS THE AUTHORITY ON WHETHER THIS IS ALLOWED, and nothing here
 * pre-checks. Route 53 refuses to delete a zone still holding records beyond
 * the NS and SOA it created itself; that refusal arrives as ZONE_NOT_EMPTY.
 *
 * A client-side emptiness check was considered and rejected for the same
 * reasons the API rejected a server-side one (FNP-1219): it duplicates a rule
 * the provider owns, costs another network call, and can disagree with the
 * provider when a record is added in between. The refusal is the check.
 */
export const deleteZone = async (
    input: DeleteDnsZoneInput,
): Promise<DeleteDnsZoneResult | undefined> => {

    const response = await client.graphql<GraphQLQuery<DeleteDnsZone>>({
        query: deleteDnsZone,
        variables: {input},
    });

    return response.data?.deleteDnsZone;
};

/**
 * The connections a zone could be created through.
 *
 * ⚠️ RETURNS EVERY CONNECTION, unfiltered. The narrowing to "can actually
 * create a zone" happens in the hook (`useZoneCreateConnections`), not here,
 * so the reason a connection was excluded stays inspectable and testable rather
 * than being silently dropped at the transport.
 */
export const fetchConnections = async (
    limit: number = 100,
    nextToken: string | null = null,
): Promise<ZoneCreateConnection[]> => {

    const response = await client.graphql<GraphQLQuery<ListProviderConnections>>({
        query: listProviderConnectionsForZoneCreate,
        variables: {
            limit,
            nextToken,
        },
    });

    return response.data?.listProviderConnections?.items ?? [];
};
